import type { UesFieldDef } from "../domain/entities/doc-type";

export type FieldPath = number[];

export function emptyField(): UesFieldDef {
  return {
    name: "",
    label: "",
    description: "",
    required: false,
    type: "string",
    isList: false,
    enumValues: null,
    fields: null,
  };
}

export function depthOf(path: FieldPath): number {
  return path.length;
}

export function canNest(path: FieldPath): boolean {
  return depthOf(path) < 2;
}

export function addField(
  tree: UesFieldDef[],
  parentPath: FieldPath,
): UesFieldDef[] {
  if (parentPath.length === 0) {
    return [...tree, emptyField()];
  }

  if (!canNest(parentPath)) {
    return tree;
  }

  const [index, ...rest] = parentPath;
  return tree.map((field, i) => {
    if (i !== index) {
      return field;
    }
    const childFields = field.fields ?? [];
    return {
      ...field,
      fields: addField(childFields, rest),
    };
  });
}

export function removeAt(tree: UesFieldDef[], path: FieldPath): UesFieldDef[] {
  if (path.length === 0) {
    return tree;
  }

  const [index, ...rest] = path;
  if (rest.length === 0) {
    return tree.filter((_, i) => i !== index);
  }

  return tree.map((field, i) => {
    if (i !== index) {
      return field;
    }
    return {
      ...field,
      fields: removeAt(field.fields ?? [], rest),
    };
  });
}

export function updateAt(
  tree: UesFieldDef[],
  path: FieldPath,
  patch: Partial<UesFieldDef>,
): UesFieldDef[] {
  if (path.length === 0) {
    return tree;
  }

  const [index, ...rest] = path;
  if (rest.length === 0) {
    return tree.map((field, i) =>
      i === index ? { ...field, ...patch } : field,
    );
  }

  return tree.map((field, i) => {
    if (i !== index) {
      return field;
    }
    return {
      ...field,
      fields: updateAt(field.fields ?? [], rest, patch),
    };
  });
}
